import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { DocumentModal, type EditJob } from '@/components/DocumentModal/DocumentModal';
import { FileFilterBar } from '@/components/FileFilterBar/FileFilterBar';
import { EMPTY_FILTERS, type FileFilters } from '@/components/FileFilterBar/FileFilterBar.types';
import { FileGrid } from '@/components/FileGrid/FileGrid';
import { ME_QUERY, type MeQueryResult } from '@/graphql/keystone';
import {
  BINDERY_JOB_QUERY,
  DELETE_FILE,
  EDIT_FILE,
  FILES_QUERY,
  FILE_DOCUMENT_QUERY,
  RAIL_PAGE_LIMIT,
  REVERT_FILE_VERSION,
  type BinderyJobQueryResult,
  type DeleteFileResult,
  type EditFileResult,
  type EditFileVariables,
  type FileDocumentQueryResult,
  type FilesQueryResult,
  type FilesQueryVariables,
  type RevertFileVersionResult,
} from '@/graphql/files';
import { isJobInFlight, type FileEditInput } from '@/lib/page-edits';
import { useToast } from '@/toast/useToast';

import styles from './FilesContainer.module.css';

const PAGE_SIZE = 24;
const JOB_POLL_MS = 3000;

/**
 * The files harness: your files in this environment's Keystone, and what
 * happened to each of them on the way through the pipeline.
 *
 * **Scoped to the signed-in person.** `createdById` is pinned to `me.id` here
 * rather than offered as a filter: a control that could widen the page past
 * the caller's own files is not a filter, it is a way around the rule.
 * Keystone's `files` can still answer for the whole platform — it has to,
 * because app-api's own callers hold no Keystone principal while
 * authorization is in flight — and this page is a caller that chooses to be
 * scoped.
 *
 * That pin is why the listing waits for `me`: Keystone reads
 * `createdById: null` as "every owner", so asking before the id arrives would
 * render the whole platform for a frame.
 *
 * One thing to know while reading an empty page: a file uploaded through
 * app-web is owned by the Person app-api resolved, which is not necessarily
 * the Person a Turnstile login resolves to — Keystone stitches its two id
 * spaces only when a token carries both keys. That split is tracked in
 * LKB-1640 and is being fixed at the identity layer rather than worked around
 * here.
 */
export function FilesContainer() {
  const toast = useToast();
  const [filters, setFilters] = useState<FileFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [openFileId, setOpenFileId] = useState<string | null>(null);
  // Deleted in this session. Keystone's delete is a soft one and the listing is
  // deliberately *not* refetched: seeing the row go grey where it was is the
  // feedback, and a row that vanished would leave nothing to read the outcome
  // off. The refetch happens when the reader reloads.
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [deleteErrors, setDeleteErrors] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editJobs, setEditJobs] = useState<ReadonlyMap<string, EditJob>>(() => new Map());
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const me = useQuery<MeQueryResult>(ME_QUERY);
  const personId = me.data?.me.id ?? null;

  const variables = useMemo<FilesQueryVariables>(
    () => ({
      ...toVariables(filters),
      createdById: personId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [filters, page, personId],
  );

  const list = useQuery<FilesQueryResult, FilesQueryVariables>(FILES_QUERY, {
    variables,
    skip: personId === null,
  });
  const document = useQuery<FileDocumentQueryResult, { id: string; railLimit: number }>(
    FILE_DOCUMENT_QUERY,
    {
      variables: { id: openFileId ?? '', railLimit: RAIL_PAGE_LIMIT },
      skip: !openFileId,
      // Shown from the cache at once, then read again: an edit that landed
      // while the modal was closed moved the head without this page knowing.
      fetchPolicy: 'cache-and-network',
    },
  );

  const [deleteFile] = useMutation<DeleteFileResult, { id: string }>(DELETE_FILE);
  const [editFile] = useMutation<EditFileResult, EditFileVariables>(EDIT_FILE);
  const [revertFileVersion] = useMutation<
    RevertFileVersionResult,
    { fileId: string; versionNo: number }
  >(REVERT_FILE_VERSION);

  // Only the open file's edit is watched; a closed modal has nobody to tell.
  const trackedEdit = openFileId ? (editJobs.get(openFileId) ?? null) : null;
  const jobQuery = useQuery<BinderyJobQueryResult, { id: string }>(BINDERY_JOB_QUERY, {
    variables: { id: trackedEdit?.job.id ?? '' },
    skip: !trackedEdit,
    pollInterval: isJobInFlight(trackedEdit?.job) ? JOB_POLL_MS : 0,
    notifyOnNetworkStatusChange: false,
  });
  const polledJob =
    jobQuery.data?.binderyJob.id === trackedEdit?.job.id ? jobQuery.data?.binderyJob : undefined;
  const openEdit = useMemo<EditJob | null>(
    () => (trackedEdit ? { ...trackedEdit, job: polledJob ?? trackedEdit.job } : null),
    [trackedEdit, polledJob],
  );

  // Once the job answers, keep the answer (which is what stops the poll) and
  // read the file again, and the listing, whose thumbnail is the head's.
  const answeredJobIds = useRef(new Set<string>());
  const refetchDocument = document.refetch;
  const refetchList = list.refetch;
  useEffect(() => {
    if (!openFileId || !openEdit || isJobInFlight(openEdit.job)) {
      return;
    }
    if (isJobInFlight(trackedEdit?.job)) {
      setEditJobs((jobs) => new Map(jobs).set(openFileId, openEdit));
    }
    if (answeredJobIds.current.has(openEdit.job.id)) {
      return;
    }
    answeredJobIds.current.add(openEdit.job.id);
    if (openEdit.job.status === 'COMPLETE' && openEdit.job.verdict === 'ok') {
      toast.success({ title: `Version ${openEdit.versionNo} is live` });
    } else {
      toast.warning({
        title: 'The edit did not land',
        description: 'The file still serves what it served before.',
      });
    }
    void refetchDocument();
    void refetchList();
  }, [openFileId, openEdit, trackedEdit, refetchDocument, refetchList, toast]);

  const onSaveEdit = useCallback(
    async (fileId: string, edits: FileEditInput, expectedVersion: number | null) => {
      setEditSaving(true);
      setEditError(null);
      try {
        const result = await editFile({ variables: { fileId, edits, expectedVersion } });
        const payload = result.data?.editFile;
        if (!payload) {
          throw new Error('Keystone answered with no edit.');
        }
        setEditJobs((jobs) =>
          new Map(jobs).set(fileId, { versionNo: payload.versionNo, job: payload.job }),
        );
        toast.info({
          title: 'Edit sent to Bindery',
          description: `Version ${payload.versionNo} will appear once it lands.`,
        });
        return true;
      } catch (cause) {
        setEditError(cause instanceof Error ? cause.message : String(cause));
        return false;
      } finally {
        setEditSaving(false);
      }
    },
    [editFile, toast],
  );

  const onRevert = useCallback(
    async (fileId: string) => {
      setReverting(true);
      setRevertError(null);
      try {
        await revertFileVersion({ variables: { fileId, versionNo: 1 } });
        // The last edit's outcome is no longer what the document shows.
        setEditJobs((jobs) => without(jobs, fileId));
        await Promise.all([refetchDocument(), refetchList()]);
        toast.success({ title: 'Reverted to the original' });
      } catch (cause) {
        setRevertError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setReverting(false);
      }
    },
    [revertFileVersion, refetchDocument, refetchList, toast],
  );

  const onOpen = useCallback((fileId: string | null) => {
    setOpenFileId(fileId);
    setEditError(null);
    setRevertError(null);
  }, []);

  const onDelete = useCallback(
    async (fileId: string) => {
      setDeletingId(fileId);
      setDeleteErrors((errors) => without(errors, fileId));
      try {
        await deleteFile({ variables: { id: fileId } });
        setDeletedIds((ids) => new Set(ids).add(fileId));
        toast.success({ title: 'File deleted' });
      } catch (cause) {
        // Surfaced per file rather than as one banner: several deletes can be
        // in flight down a long list, and a message detached from its row does
        // not say which one failed.
        setDeleteErrors((errors) =>
          new Map(errors).set(fileId, cause instanceof Error ? cause.message : String(cause)),
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deleteFile, toast],
  );

  const files = personId === null ? [] : (list.data?.files ?? []);
  // The listing has no total, so "is there another page" is answered by having
  // been given a full one. A short page is the last page.
  const hasMore = files.length === PAGE_SIZE;

  const onFiltersChange = (next: FileFilters) => {
    setFilters(next);
    setPage(0);
  };

  return (
    <section>
      <h2 className={styles.heading}>Files</h2>
      <p className={styles.lede}>
        The Files you own in this environment's Keystone. Open one to see the page rail Bindery
        rendered and everything the row says about itself.
      </p>

      <FileFilterBar
        filters={filters}
        onChange={onFiltersChange}
        onReset={() => onFiltersChange(EMPTY_FILTERS)}
      />

      <FileGrid
        deleteErrors={deleteErrors}
        deletedIds={deletedIds}
        deletingId={deletingId}
        error={(me.error ?? list.error)?.message ?? null}
        files={files}
        loading={me.loading || list.loading}
        onDelete={onDelete}
        onOpen={onOpen}
      />

      <div className={styles.pager}>
        <Button
          disabled={page === 0}
          onClick={() => setPage((n) => Math.max(0, n - 1))}
          variant="ghost"
        >
          Previous
        </Button>
        <span className={styles.pageNumber}>Page {page + 1}</span>
        <Button disabled={!hasMore} onClick={() => setPage((n) => n + 1)} variant="ghost">
          Next
        </Button>
      </div>

      {openFileId ? (
        <DocumentModal
          key={openFileId}
          deleteError={deleteErrors.get(openFileId) ?? null}
          deleted={deletedIds.has(openFileId)}
          deleting={deletingId === openFileId}
          error={document.error ? document.error.message : null}
          file={document.data?.file ?? null}
          loading={document.loading}
          editError={editError}
          editJob={openEdit}
          editSaving={editSaving}
          onStartEdit={() => setEditError(null)}
          onClose={() => onOpen(null)}
          onDelete={onDelete}
          onRevert={onRevert}
          onSaveEdit={onSaveEdit}
          railLimit={RAIL_PAGE_LIMIT}
          revertError={revertError}
          reverting={reverting}
        />
      ) : null}
    </section>
  );
}

function without<T>(entries: ReadonlyMap<string, T>, key: string): ReadonlyMap<string, T> {
  const next = new Map(entries);
  next.delete(key);
  return next;
}

/**
 * The filter bar speaks in the three-way words a person reads; Keystone speaks
 * in nullable booleans. Translating here keeps the component tellable through
 * props and keeps `undefined` — "do not filter" — out of the UI's vocabulary.
 *
 * `createdById` is absent deliberately: it is the caller's, not the filter
 * bar's, and the container adds it after this runs so nothing here can clear
 * it.
 */
function toVariables(
  filters: FileFilters,
): Omit<FilesQueryVariables, 'limit' | 'offset' | 'createdById'> {
  const tri = (value: 'any' | string, truthy: string) =>
    value === 'any' ? null : value === truthy;

  return {
    status: filters.status || null,
    extAppApiId: filters.extAppApiId.trim() || null,
    hasExtAppApiId: tri(filters.appApiRef, 'present'),
    migrated: tri(filters.origin, 'migrated'),
    canonicalized: tri(filters.canonical, 'present'),
    canonicalization: filters.canonicalization || null,
    // Documents only. A canonical PDF and an extraction JSON are File rows in
    // their own right, so an unfiltered listing interleaves every document
    // with the artifact made from it — and an artifact never carries an
    // `extAppApiId`, which is the very condition the delete control keys off,
    // so leaving them in offers a delete on exactly the rows that must not
    // have one: deleting a canonical strands its parent with no preview and
    // no way to get one back.
    derived: false,
  };
}
